import { useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { tokens } from '@/theme'
import type { Comment } from '@/domain/types'
import { useCommentActions } from '@/api/hooks/useInitiativeChildren'
import { ErrorPanel } from '@/components/feedback'

interface Props {
  initiativeId: number
  comments: Comment[]
  /** Signed-in user's Entra OID — decides which comments are editable. */
  userOid: string | null
  isAdministrator: boolean
  /** Bumped by the parent to re-fetch the list and the activity feed. */
  onChanged: () => void
}

/**
 * Comment thread.
 *
 * Any `Reader` can post — the people with the most useful thing to say
 * about an initiative are business people who will never hold
 * `InitiativeContributor`.
 *
 * Edit and delete are your own only (Administrators can remove anything).
 * Delete is a soft delete: the comment leaves the page, the temporal
 * history keeps the text. In an auditable registry that is the right trade,
 * and it is why the comment box is allowed to have an edit button at all —
 * nothing is actually lost.
 */
export function CommentBox({ initiativeId, comments, userOid, isAdministrator, onChanged }: Props) {
  const [draft, setDraft] = useState('')
  const actions = useCommentActions()
  const busy = actions.state.status === 'loading'

  const post = async (): Promise<void> => {
    const body = draft.trim()
    if (!body) return
    const result = await actions.run(`/api/initiatives/${initiativeId}/comments`, 'POST', { body })
    if (result !== null) {
      setDraft('')
      onChanged()
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gap: 8 }}>
        <textarea
          value={draft}
          onChange={e => setDraft(e.currentTarget.value)}
          rows={3}
          maxLength={4000}
          aria-label="Add a comment"
          placeholder="Add a comment…"
          style={{
            padding: '11px 13px',
            fontSize: 14,
            fontFamily: tokens.fontDisplay,
            color: tokens.ink,
            background: tokens.paper,
            border: `1px solid ${tokens.line2}`,
            borderRadius: tokens.radiusMd,
            resize: 'vertical',
            minHeight: 74,
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            disabled={busy || draft.trim() === ''}
            onClick={() => void post()}
            style={{
              background: tokens.gold,
              color: tokens.paper,
              border: 'none',
              borderRadius: tokens.radiusMd,
              padding: '9px 18px',
              fontFamily: tokens.fontDisplay,
              fontSize: 12,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: busy || draft.trim() === '' ? 'not-allowed' : 'pointer',
              opacity: busy || draft.trim() === '' ? 0.5 : 1,
            }}
          >
            {busy ? 'Posting…' : 'Post comment'}
          </button>
          <span style={{ fontSize: 11, color: tokens.ink4, fontFamily: tokens.fontDisplay }}>
            Stamped with the stage this initiative is at right now.
          </span>
        </div>
        {actions.state.status === 'error' && (
          <ErrorPanel error={actions.state.error} maxWidth="100%" />
        )}
      </div>

      {comments.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: tokens.ink4,
            fontFamily: tokens.fontDisplay,
          }}
        >
          No comments yet.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          {comments.map(comment => (
            <CommentRow
              key={comment.commentId}
              comment={comment}
              initiativeId={initiativeId}
              canManage={
                isAdministrator ||
                (userOid !== null && comment.authorObjectId.toLowerCase() === userOid.toLowerCase())
              }
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function CommentRow({
  comment,
  initiativeId,
  canManage,
  onChanged,
}: {
  comment: Comment
  initiativeId: number
  canManage: boolean
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.body)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const actions = useCommentActions()
  const busy = actions.state.status === 'loading'

  const save = async (): Promise<void> => {
    const body = draft.trim()
    if (!body) return
    const result = await actions.run(
      `/api/initiatives/${initiativeId}/comments/${comment.commentId}`,
      'PATCH',
      { body },
    )
    if (result !== null) {
      setEditing(false)
      onChanged()
    }
  }

  const remove = async (): Promise<void> => {
    // DELETE returns 204, so `apiFetch` resolves `undefined` and `run`
    // passes that through. `null` is the failure signal — reading
    // `actions.state` here would see the pre-dispatch value.
    const result = await actions.run(
      `/api/initiatives/${initiativeId}/comments/${comment.commentId}`,
      'DELETE',
    )
    if (result !== null) {
      setConfirmingDelete(false)
      onChanged()
    }
  }

  return (
    <li
      style={{
        padding: '12px 16px',
        background: tokens.paper,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: tokens.weightDemi, color: tokens.ink }}>
          {comment.authorDisplayName || '—'}
        </span>
        <span style={{ fontSize: 11.5, color: tokens.ink4, fontVariantNumeric: 'tabular-nums' }}>
          {new Date(comment.createdAt).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        <span
          title="Stage at the time of writing"
          style={{
            fontSize: 9.5,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: tokens.ink3,
            background: tokens.paper3,
            border: `1px solid ${tokens.line2}`,
            borderRadius: tokens.radiusSm,
            padding: '1px 6px',
          }}
        >
          {comment.stageName}
        </span>
        {comment.edited && (
          <span style={{ fontSize: 11, color: tokens.ink4, fontStyle: 'italic' }}>edited</span>
        )}
        <span style={{ flex: 1 }} />
        {canManage && !editing && (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <IconButton label="Edit comment" onClick={() => setEditing(true)}>
              <Pencil size={12} strokeWidth={1.7} />
            </IconButton>
            <IconButton label="Delete comment" onClick={() => setConfirmingDelete(true)}>
              <Trash2 size={12} strokeWidth={1.7} />
            </IconButton>
          </span>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.currentTarget.value)}
            rows={3}
            maxLength={4000}
            aria-label="Edit comment"
            style={{
              padding: '10px 12px',
              fontSize: 13,
              fontFamily: tokens.fontDisplay,
              color: tokens.ink,
              background: tokens.paper,
              border: `1px solid ${tokens.line2}`,
              borderRadius: tokens.radiusMd,
              resize: 'vertical',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <SmallButton primary disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </SmallButton>
            <SmallButton
              disabled={busy}
              onClick={() => {
                setDraft(comment.body)
                setEditing(false)
              }}
            >
              Cancel
            </SmallButton>
          </div>
        </div>
      ) : (
        <p
          style={{
            margin: 0,
            fontSize: 13.5,
            color: tokens.ink2,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          }}
        >
          {comment.body}
        </p>
      )}

      {confirmingDelete && (
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 12.5, color: tokens.ink2 }}>
            Remove this comment from the page? The text stays in the audit history.
          </span>
          <SmallButton primary disabled={busy} onClick={() => void remove()}>
            {busy ? 'Removing…' : 'Remove'}
          </SmallButton>
          <SmallButton disabled={busy} onClick={() => setConfirmingDelete(false)}>
            Cancel
          </SmallButton>
        </div>
      )}

      {actions.state.status === 'error' && (
        <div style={{ marginTop: 10 }}>
          <ErrorPanel error={actions.state.error} maxWidth="100%" />
        </div>
      )}
    </li>
  )
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        background: 'transparent',
        border: `1px solid ${tokens.line2}`,
        borderRadius: tokens.radiusSm,
        color: tokens.ink3,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

function SmallButton({
  children,
  onClick,
  disabled,
  primary,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
  primary?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: primary ? tokens.gold : 'transparent',
        color: primary ? tokens.paper : tokens.ink2,
        border: primary ? 'none' : `1px solid ${tokens.line2}`,
        borderRadius: tokens.radiusMd,
        padding: '7px 14px',
        fontFamily: tokens.fontDisplay,
        fontSize: 11.5,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {children}
    </button>
  )
}
